import { type Ref, useState } from 'react';

import ActionButton from 'components/ActionButton';
import Button from 'components/Button';
import Img from 'components/Img';
import { Checkbox } from 'components/InputUi';
import { OptionalLink } from 'components/Link';
import Message, { CartErrorMessage } from 'components/Message';
import Price, { hasReducedPrice, PriceText } from 'components/Price';
import QuantityLabel from 'components/QuantityLabel';
import Staffling from 'components/Staffling';
import StepInput from 'components/StepInput';
import { ShelfInformation, StockStatus } from 'components/Stock';
import Text from 'components/Text';
import { usePriceContext } from 'contexts';
import { useFeatureToggle } from 'hooks';
import type { BasketRowResponse, CartError } from 'models/api';
import type { Branded } from 'types';
import { getProductImage } from 'utils/business-logic';
import { cn } from 'utils/classNames';
import { getTestDataAttrFrom, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import BasketRowPrintInfo from './BasketRowPrintInfo';

export type BasketRowId = Branded<string, 'BasketRowId'>;

export function getBasketRowId(row: BasketRowResponse) {
	return [row.variantId, row.customization?.customization?.id]
		.filter(Boolean)
		.join('-') as BasketRowId;
}

export type SubtitleVariant = 'article-number' | 'brand-model';

interface Props {
	className?: string;
	containerElement?: 'article' | 'li';
	errors?: CartError[];
	hasInvalidQuantity?: boolean;
	hasShelfInformation?: boolean;
	hasStoreStock?: boolean;
	moveToCartButtonText?: string;
	moveToWishlistButtonText?: string;
	onMoveToCart?: () => Promise<void>;
	onMoveToWishlist?: () => Promise<void>;
	onPickedChange?: () => void;
	onQuantityChange?: (qty: number | undefined) => void;
	onRemove?: () => Promise<void> | void;
	onRemoveConfirm?: () => Promise<void>;
	onRemoveConfirmCancel?: () => void;
	pickedCheckboxLabel?: string;
	ref?: Ref<HTMLElement>;
	removeButtonRef?: Ref<HTMLButtonElement>;
	removeButtonText?: string;
	removeConfirmationText?: string;
	removeConfirmationTextRef?: Ref<HTMLParagraphElement>;
	row: BasketRowResponse;
	subtitleVariant: SubtitleVariant;
}

export default function BasketRow({
	className,
	containerElement: ContainerElement = 'article',
	errors,
	hasInvalidQuantity = false,
	hasShelfInformation = false,
	hasStoreStock: hasStoreStockProp = false,
	moveToCartButtonText,
	moveToWishlistButtonText,
	onMoveToCart,
	onMoveToWishlist,
	onPickedChange,
	onQuantityChange,
	onRemove,
	onRemoveConfirm,
	onRemoveConfirmCancel,
	pickedCheckboxLabel,
	ref,
	removeButtonRef,
	removeButtonText,
	removeConfirmationText,
	removeConfirmationTextRef,
	row,
	subtitleVariant,
}: Props) {
	const { t } = useI18n();
	const { storesEnabled } = useFeatureToggle();
	const { usePriceExcVat } = usePriceContext();
	const [hasEmptyQuantity, setHasEmptyQuantity] = useState<boolean>(false);

	const {
		brand,
		category,
		model,
		quantity,
		rowSummary,
		storeStock,
		title,
		url,
		variantId,
		volumePrice,
		webStock,
	} = row;
	const image = getProductImage(row.images);
	// Picked status is only relevant when the checkbox is present so also
	// check for the change handler.
	const isPicked = Boolean(row.picked && onPickedChange);

	const isConfirmingRemove = !!(onRemoveConfirm && onRemoveConfirmCancel);

	const rowSummaryPrice = usePriceExcVat
		? rowSummary?.priceExcVat
		: rowSummary?.priceIncVat;

	const rowId = getBasketRowId(row);
	const titleId = `basket-row-title-${rowId}`;

	const customizationPlacements =
		row.customization?.customization?.placements ?? [];
	const hasStoreStock = hasStoreStockProp && !!storeStock && storesEnabled;
	const hasShelf =
		hasShelfInformation &&
		storesEnabled &&
		Boolean(storeStock?.locations?.length);
	const isInvalidQuantity =
		hasInvalidQuantity ||
		errors?.some(({ type }) => type === 'VariantInsufficientStock');

	return (
		<ContainerElement
			// @ts-expect-error HTMLElement is missing props specific to <li> which
			// will never be used anyway.
			ref={ref}
			aria-labelledby={titleId}
			className={cn(
				'@container/basket-row flex items-start print:break-inside-avoid',
				// Wrap when small enough, to handle zoomed mobile screens.
				'max-[20rem]:flex-wrap',
				className,
			)}
		>
			{onPickedChange && (
				<Checkbox
					id={`picked-${rowId}`}
					label={pickedCheckboxLabel}
					hiddenLabel
					checked={isPicked}
					onChange={onPickedChange}
					// Top margin to center align with the image.
					className="relative z-1 mt-3 mr-2 shrink-0 @basketMd/basket-row:mt-7"
					labelClassName={cn(
						// Add a larger hit area for the checkbox.
						'before:absolute before:-inset-y-4 before:-right-8 before:-left-2 before:z-1 @basketMd/basket-row:before:-inset-y-8',
						// Add a hover feedback circle.
						'hover:after:absolute hover:after:-inset-3 hover:after:-z-1 hover:after:rounded-full hover:after:bg-subtle',
					)}
				/>
			)}

			<OptionalLink
				href={url}
				// Right padding instead of margin on purpose to have a continuous
				// click area to the title. Bottom padding for the very small screen
				// (or zoom triggered) wrapping above.
				className="shrink-0 pr-4 pb-2"
				linkClassName="peer"
				isScreenReaderHidden
			>
				<Img
					className={cn(
						// On small screens, the 'image column' stays at 80 with and
						// without the picked checkbox, so shrink the image if the
						// checkbox is present.
						// Always 80 on large screens.
						onPickedChange ? 'size-12' : 'size-20',
						'@basketMd/basket-row:size-20',
					)}
					src={image?.src}
					alt={image?.alt}
					width={96}
					height={96}
				/>
			</OptionalLink>

			<div className="grow peer-hover:**:data-title:underline @basketMd/basket-row:pt-2">
				<div
					className={cn(
						'@basketMd/basket-row:grid @basketMd/basket-row:items-start @basketMd/basket-row:gap-6',
						hasShelf
							? '@basketMd/basket-row:grid-cols-3'
							: '@basketMd/basket-row:grid-cols-2',
					)}
				>
					<div>
						<header>
							<OptionalLink
								href={url}
								id={titleId}
								className="inline-block font-bold"
								linkClassName="hover:underline"
								hasExpandedHitArea
								// Used for hover feedback where hovering the image link will
								// underline the title, see above.
								data-title
							>
								{title}
							</OptionalLink>
							{subtitleVariant === 'brand-model' && (
								<Text as="pSmall" className="mt-1">
									{brand?.title}
									{model && (
										<>
											<span aria-hidden="true"> | </span>
											{model}
										</>
									)}
								</Text>
							)}
							{subtitleVariant === 'article-number' && (
								<Text as="pSmall" className="mt-1">
									{t('general_article_number_label')} {variantId}
								</Text>
							)}
						</header>

						{!isPicked && (
							<>
								<BasketRowPrintInfo
									className="mt-4"
									placements={customizationPlacements}
								/>

								{row.price && (
									<>
										<Price price={row.price} size="md" className="mt-4" />
										{usePriceExcVat && (
											<Text
												className="mt-1"
												text={t('price_vat_excluded_text')}
												as="pXSmall"
											/>
										)}
									</>
								)}

								{Boolean(webStock || hasStoreStock) && (
									<div className="mt-4 space-y-1">
										{webStock && (
											<StockStatus
												size="sm"
												inStock={webStock.inStock}
												text={webStock.text.generic}
											/>
										)}
										{hasStoreStock && (
											<StockStatus
												size="sm"
												hasStoreSelector
												inStock={storeStock.inStock}
												text={storeStock.text.specific}
												variantId={variantId}
											/>
										)}
									</div>
								)}
							</>
						)}
					</div>

					{!isPicked && (
						<>
							{hasShelf && (
								<ShelfInformation
									className="@max-basketMd/basket-row:hidden"
									categoryColor={category?.color}
									categoryName={category?.name}
									locations={storeStock?.locations}
								/>
							)}

							<div className="flex flex-wrap items-center justify-between gap-4 @max-basketMd/basket-row:mt-4">
								{onQuantityChange && (
									<>
										<StepInput
											size="md"
											value={hasEmptyQuantity ? undefined : quantity}
											id={`quantity-${rowId}`}
											onChange={(newQuantity) => {
												// Handle emptying the input field, passing that on to
												// the cart/wishlist is a no-op.
												if (newQuantity) {
													setHasEmptyQuantity(false);
													onQuantityChange(newQuantity);
												} else {
													setHasEmptyQuantity(true);
												}
											}}
											onInputBlur={() => {
												setHasEmptyQuantity(false);
											}}
											invalid={isInvalidQuantity}
											className="max-w-40 @basketMd/basket-row:max-w-48 print:hidden"
										/>
										<QuantityLabel
											className="not-print:hidden"
											quantity={quantity}
										/>
									</>
								)}
								{!onQuantityChange && <QuantityLabel quantity={quantity} />}

								<PriceText
									price={rowSummaryPrice}
									isReducedPrice={hasReducedPrice(rowSummary?.priceType)}
									className="font-bold"
									data-cy={getTestDataAttrFrom(`row-sum-${variantId}`)}
								/>
							</div>
						</>
					)}
				</div>

				{!isPicked && (
					<>
						{hasShelf && (
							<ShelfInformation
								className="mt-4 @basketMd/basket-row:hidden"
								categoryColor={category?.color}
								categoryName={category?.name}
								locations={storeStock?.locations}
							/>
						)}

						<div className="mt-4 flex flex-wrap gap-6 empty:hidden print:hidden">
							{onMoveToWishlist && (
								<ActionButton
									variant="text"
									size="sm"
									hasExpandedHitArea
									onClick={onMoveToWishlist}
									data-cy={getTestDataAttrFrom(
										`move-variant-wishlist-${variantId}`,
									)}
								>
									{moveToWishlistButtonText}
								</ActionButton>
							)}
							{onMoveToCart && (
								<ActionButton
									variant="text"
									size="sm"
									hasExpandedHitArea
									onClick={onMoveToCart}
									data-cy={getTestDataAttrFrom(
										`move-variant-cart-${variantId}`,
									)}
								>
									{moveToCartButtonText}
								</ActionButton>
							)}
							{onRemove && !isConfirmingRemove && (
								<ActionButton
									ref={removeButtonRef}
									variant="text"
									size="sm"
									hasExpandedHitArea
									onClick={onRemove}
									data-cy={getTestDataAttrFrom(`remove-variant-${variantId}`)}
								>
									{removeButtonText}
								</ActionButton>
							)}
						</div>

						{isConfirmingRemove && (
							<Message variant="warning" className="mt-4">
								<p ref={removeConfirmationTextRef} tabIndex={-1}>
									{removeConfirmationText}
								</p>
								<div className="mt-3 flex flex-wrap items-center gap-4">
									<ActionButton
										variant="branded"
										size="sm"
										onClick={onRemoveConfirm}
										data-cy={getTestDataAttrFrom(
											`confirm-remove-variant-${variantId}`,
										)}
									>
										{removeButtonText}
									</ActionButton>
									<Button
										variant="text"
										size="sm"
										onClick={onRemoveConfirmCancel}
										hasExpandedHitArea
									>
										{t('general_cancel_button_text')}
									</Button>
								</div>
							</Message>
						)}

						{volumePrice && (
							<Staffling volumePrice={volumePrice} className="mt-4" />
						)}
						{is.arrayWithLength(errors) && (
							<div className="mt-6 space-y-2">
								{errors.map((error) => (
									<CartErrorMessage
										key={`${error.key}-${error.type}-${error.level}`}
										error={error}
									/>
								))}
							</div>
						)}
					</>
				)}
			</div>
		</ContainerElement>
	);
}
BasketRow.displayName = 'BasketRow';
